import React, { createContext, useState, useContext, useEffect } from 'react';
import { ApiActions } from '../shared/apiActions';
import { loadCurrencies } from '../shared/currencyService';
import { clearAccountCache, syncTourStatusFromAccount } from '../shared/accountCache';
import apiCall from '../shared/apiCall';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currenciesLoaded, setCurrenciesLoaded] = useState(false);

  // Load currencies when authenticated
  const initializeCurrencies = async () => {
    if (currentUser && !currenciesLoaded) {
      try {
        await loadCurrencies();
        setCurrenciesLoaded(true);
      } catch (error) {
        console.error('Error loading currencies:', error);
      }
    }
  };

  // Auto-set cookie consent when user logs in
  // (By logging in, user implicitly agrees to use cookies as stated in Privacy Policy)
  const setCookieConsentIfNeeded = () => {
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (!cookieConsent) {
      localStorage.setItem('cookieConsent', 'accepted');
      localStorage.setItem('cookieConsentDate', new Date().toISOString());
    }
  };

  useEffect(() => {
    // Check if user is logged in on initial load
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (token && userData) {
      try {
        const user = JSON.parse(userData);
        setCurrentUser(user);
        
        // Auto-set cookie consent if user is already logged in
        setCookieConsentIfNeeded();
        
        // Initialize currencies after setting user
        initializeCurrencies();
      } catch (e) {
        console.error('Error parsing user data:', e);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    
    setLoading(false);
  }, []);

  // Effect to initialize currencies when user changes
  useEffect(() => {
    initializeCurrencies();
  }, [currentUser]);

  // Login function
  const login = async (email, password) => {
    try {
      setError('');
      
      const data = await apiCall(`/api/set?action=${ApiActions.LOGIN_USER}`, { email, password }, 'POST');
      
      if (data.success) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        setCurrentUser(data.user);
        
        // Auto-set cookie consent when user logs in
        setCookieConsentIfNeeded();
        
        // Sync tour status from account to localStorage AND refresh account cache
        // We await this to ensure account data is fresh before redirect
        await syncTourStatusFromAccount().catch(err => console.error('Error syncing tour status:', err));
        
        // Load currencies after successful login
        await initializeCurrencies();
        
        return true;
      } else {
        setError(data.message || 'Login failed');
        return false;
      }
    } catch (err) {
      setError(err.message || 'An error occurred during login');
      return false;
    }
  };

  // Register function
  const register = async (name, email, password) => {
    try {
      setError('');
      
      // apiCall automatically adds X-Language header from localStorage
      const data = await apiCall(`/api/set?action=${ApiActions.REGISTER_USER}`, { name, email, password }, 'POST');
      
      if (data.success) {
        return true;
      } else {
        setError(data.message || 'Registration failed');
        return false;
      }
    } catch (err) {
      setError(err.message || 'An error occurred during registration');
      return false;
    }
  };

  // Google Login function
  const googleLogin = async (credential) => {
    try {
      setError('');
      
      // apiCall automatically adds X-Language header from localStorage
      const data = await apiCall(`/api/set?action=${ApiActions.GOOGLE_AUTH}`, { credential }, 'POST');
      
      if (data.success) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        setCurrentUser(data.user);
        
        // Auto-set cookie consent when user logs in
        setCookieConsentIfNeeded();
        
        // Sync tour status from account to localStorage AND refresh account cache
        // We await this to ensure account data is fresh before redirect
        await syncTourStatusFromAccount().catch(err => console.error('Error syncing tour status:', err));
        
        // Load currencies after successful login
        await initializeCurrencies();
        
        return true;
      } else {
        setError(data.message || 'Google login failed');
        return false;
      }
    } catch (err) {
      setError(err.message || 'An error occurred during Google login');
      return false;
    }
  };

  // Logout function
  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    clearAccountCache(); // Clear cached account data
    setCurrentUser(null);
    setCurrenciesLoaded(false);
  };

  const value = {
    currentUser,
    login,
    register,
    googleLogin,
    logout,
    error,
    setError,
    currenciesLoaded
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
} 